import security from "./Security";

/**
 * Shared state for the in-app help chat panel.
 *
 * The header menu item (rendered by every page) and the panel (rendered once in
 * App.js) both talk to this store, so they agree on whether the panel is open.
 * Navigation in the app is done with full page loads, so the state is kept in
 * web storage:
 *
 * - whether the panel is open lives in sessionStorage, so it follows the person
 *   from page to page in one tab but does not open by itself in a new tab (for
 *   example the tab a citation opens);
 * - the active conversation id lives in localStorage keyed by user, so closing
 *   and reopening the panel, even days later, returns to the same conversation.
 */
class HelpChat {
  constructor() {
    this.openKey = "helpChatOpen";
    this.listeners = [];
  }

  /**
   * Storage key for the active conversation of the signed-in user.
   */
  sessionKey = () => {
    return "helpChatSession-" + security.getUserId();
  };

  /**
   * Reads a value from web storage, treating storage errors (private mode,
   * blocked storage) as "nothing stored".
   */
  read = (storage, key) => {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  /**
   * Writes a value to web storage, removing the key when value is null.
   */
  write = (storage, key, value) => {
    try {
      if (value === null) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, value);
      }
    } catch (error) {
      // Storage is unavailable; the panel still works for this page view.
    }
  };

  isOpen = () => {
    return this.read(window.sessionStorage, this.openKey) === "true";
  };

  open = () => {
    this.write(window.sessionStorage, this.openKey, "true");
    this.notify();
  };

  close = () => {
    this.write(window.sessionStorage, this.openKey, null);
    this.notify();
  };

  toggle = () => {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  };

  /**
   * @returns {number|null} the id of the conversation the panel last showed
   */
  getSessionId = () => {
    var stored = this.read(window.localStorage, this.sessionKey());
    var sessionId = parseInt(stored, 10);
    return isNaN(sessionId) ? null : sessionId;
  };

  /**
   * @param {number|null} sessionId - null starts a fresh conversation
   */
  setSessionId = (sessionId) => {
    var value = sessionId === null ? null : String(sessionId);
    this.write(window.localStorage, this.sessionKey(), value);
  };

  /**
   * Subscribes to open/close changes.
   *
   * @param {Function} listener - called with the new open state
   * @returns {Function} unsubscribe
   */
  subscribe = (listener) => {
    var _this = this;
    this.listeners.push(listener);
    return function () {
      _this.listeners = _this.listeners.filter(function (item) {
        return item !== listener;
      });
    };
  };

  notify = () => {
    var isOpen = this.isOpen();
    this.listeners.forEach(function (listener) {
      listener(isOpen);
    });
  };
}

export default new HelpChat();
